import React from 'react';
import { Button } from './ui/button';
import { CheckCircle, Phone, MapPin } from 'lucide-react';
import { mockData } from '../mock';

export const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="accueil" className="relative min-h-screen flex items-center pt-20">
      {/* Background avec overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="absolute inset-0 bg-black/40"></div>
        {/* Texture subtile */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}>
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Contenu principal */}
          <div className="text-white space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 bg-blue-600/20 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-400/30">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                <span className="text-blue-200 font-medium">Entreprise certifiée BTP</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight whitespace-nowrap">
                <span className="bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  {mockData.company.name}
                </span>
              </h1>
              
              <p className="text-2xl lg:text-3xl text-blue-200 font-medium">
                {mockData.company.slogan}
              </p>
              
              <p className="text-xl text-slate-300 leading-relaxed max-w-2xl">
                {mockData.company.description}
              </p>
            </div>

            {/* Points forts */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-slate-300">Plans 2D & 3D</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-slate-300">Chiffrage bâtiment</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-slate-300">Architecture d'intérieur</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-slate-300">Suivi gros œuvre</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                onClick={() => window.location.href = 'tel:0650242913'}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg rounded-lg transition-all duration-300 hover:shadow-2xl hover:shadow-blue-600/25 hover:scale-105"
              >
                Nous appeler
              </Button>
              
              <Button 
                variant="outline"
                size="lg"
                onClick={() => scrollToSection('services')}
                className="border-2 border-white/30 text-white hover:bg-white hover:text-slate-800 px-8 py-4 text-lg rounded-lg transition-all duration-300 hover:scale-105"
              >
                Nos prestations
              </Button>
            </div>

            {/* Contact rapide */}
            <div className="flex flex-col sm:flex-row gap-6 pt-8 border-t border-white/20">
              <a 
                href="tel:0650242913"
                className="flex items-center space-x-3 text-slate-300 hover:text-white transition-colors group"
              >
                <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center group-hover:bg-blue-600/30 transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Appelez-nous</p>
                  <p className="font-semibold">06 50 24 29 13</p>
                </div>
              </a>
              
              <div className="flex items-center space-x-3 text-slate-300">
                <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Basés à</p>
                  <p className="font-semibold">Trappes (78190)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column - kept empty for layout balance */}
          <div className="lg:block hidden"></div>
        </div>
      </div>

      {/* Logo en haut à droite - effet 3D avec fond blanc */}
      <div className="hidden lg:block absolute top-28 right-6 xl:right-12 z-20" style={{ perspective: '800px' }}>
        <div 
          className="logo-3d relative transition-transform duration-500 ease-out hover:scale-110"
          style={{ transformStyle: 'preserve-3d', transform: 'rotateY(-18deg) rotateX(8deg)' }}
        >
          {/* Shadow / depth layer */}
          <div 
            className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-600/40 to-slate-900/60 blur-xl"
            style={{ transform: 'translateZ(-30px) translateY(10px)' }}
          ></div>
          
          {/* Back plate for depth */}
          <div 
            className="absolute inset-0 rounded-xl bg-gradient-to-br from-slate-300 to-slate-500"
            style={{ transform: 'translateZ(-8px)' }}
          ></div>

          {/* White card with logo */}
          <div className="relative bg-white/95 backdrop-blur-md rounded-xl p-2 shadow-2xl border border-white/30">
            <img 
              src="/images/logo.jpeg" 
              alt="BS Devis&Plan" 
              className="w-[88px] h-[88px] object-contain"
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};